/**
 * Phase 37 — what a drop's texts are made of. A drop sends several texts
 * (SMSWiser rotates them); the MIX says how many are PROVEN (taken from the
 * ranking's reuse pool, the best first, taking turns) and how many are
 * FRESH AI texts — on the SAME drop (user, 13/10/2026: "best 5 based on
 * the formula and 10 AI generated"). Every ordinary drop sends the ordinary
 * mix (`provenPerDrop` + the Generation step's AI texts per drop); a kind of
 * drop that is a bet (scaled, shrunk, a list's first day, a returning list,
 * an experiment's drop) may have its own mix, typically proven only. A kind
 * without a mix follows the ordinary one, so an untouched policy changes
 * nothing. One kind per drop, by precedence: the riskier reason wins (a
 * route test's drops are all route test drops: their own mix, the same
 * texts on every test drop — 19/10/2026). The
 * engine, the fill and the editor all read these functions.
 * For Media Devoted | Created By Ciro Autiero
 */
export const DROP_GEN_DROP_KINDS = ["ROUTE_TEST", "HIT_AGAIN", "RETURNING", "NEW_LIST", "EXPERIMENT", "SCALED", "SHRUNK", "PROBE", "EXTRA", "NORMAL"] as const;
export type DropGenDropKind = (typeof DROP_GEN_DROP_KINDS)[number];

/** Phase 38 — a kind that keeps the texts of the drop it came from unless the policy gives it its own mix. */
export const DROP_GEN_COPY_KINDS: readonly DropGenDropKind[] = ["HIT_AGAIN"];

/** The kinds a policy may set apart — NORMAL is "the rest"; a route test drop's texts are the route test's own choice (19/10/2026). */
export const DROP_GEN_POLICY_KINDS = DROP_GEN_DROP_KINDS.filter((k) => k !== "NORMAL" && k !== "ROUTE_TEST") as Exclude<DropGenDropKind, "NORMAL" | "ROUTE_TEST">[];
export type DropGenPolicyKind = (typeof DROP_GEN_POLICY_KINDS)[number];

/** Texts on one drop, at most this many in all (SMSWiser rotates them). */
export const DROP_GEN_TEXTS_PER_DROP_MAX = 15;

/** The texts of one drop: proven ones from the pool + fresh AI ones. */
export interface DropGenTextMix {
  proven: number;
  ai: number;
}

/** kind → its mix; a missing kind follows the ordinary mix. */
export type DropGenTextPolicy = Partial<Record<DropGenPolicyKind, DropGenTextMix>>;

/** The ordinary mix and the policy, as the fill reads them. */
export interface DropGenTextMixInput {
  policy?: DropGenTextPolicy;
  /** Proven texts on every ordinary drop. */
  provenPerDrop: number;
  /** Fresh AI texts on every ordinary drop (the Generation step's amount). */
  amount: number;
  /** 19/10/2026 — a route test's drops: its own mix, the same texts on every test drop. */
  routeTest?: DropGenTextMix;
}

/** A bet sends proven texts only: the 5 best of the pool (fewer when the pool is smaller). */
export const RECOMMENDED_BET_MIX: DropGenTextMix = { proven: 5, ai: 0 };
export const RECOMMENDED_TEXT_POLICY: DropGenTextPolicy = {
  RETURNING: RECOMMENDED_BET_MIX,
  NEW_LIST: RECOMMENDED_BET_MIX,
  EXPERIMENT: RECOMMENDED_BET_MIX,
  SCALED: RECOMMENDED_BET_MIX,
  SHRUNK: RECOMMENDED_BET_MIX,
};

/** The facts a planned drop carries that make its kind. */
export interface DropKindFacts {
  /** Phase 38 — sent again right after the drop that hit the rule. */
  hitAgain?: unknown | null;
  scaled?: { steps: number } | null;
  returnTest?: unknown | null;
  experiment?: unknown | null;
  optimizer?: { kind: "extra" | "probe" } | null;
  listFirstDay?: boolean | null;
  /** 19/10/2026 — a drop of a route test container. */
  routeTest?: boolean | null;
}

export function dropKindOf(d: DropKindFacts): DropGenDropKind {
  if (d.routeTest) return "ROUTE_TEST";
  if (d.hitAgain) return "HIT_AGAIN";
  if (d.returnTest) return "RETURNING";
  if (d.listFirstDay) return "NEW_LIST";
  if (d.experiment) return "EXPERIMENT";
  if (d.scaled && d.scaled.steps > 0) return "SCALED";
  if (d.scaled && d.scaled.steps < 0) return "SHRUNK";
  if (d.optimizer?.kind === "probe") return "PROBE";
  if (d.optimizer?.kind === "extra") return "EXTRA";
  return "NORMAL";
}

const whole = (v: unknown, max: number): number | null =>
  typeof v === "number" && Number.isFinite(v) ? Math.min(max, Math.max(0, Math.round(v))) : null;

/** Each part 0..15 and the drop never over 15 texts (the AI part gives way). */
export function clampMix(m: DropGenTextMix): DropGenTextMix {
  const proven = whole(m.proven, DROP_GEN_TEXTS_PER_DROP_MAX) ?? 0;
  const ai = Math.min(whole(m.ai, DROP_GEN_TEXTS_PER_DROP_MAX) ?? 0, DROP_GEN_TEXTS_PER_DROP_MAX - proven);
  return { proven, ai };
}

export const ordinaryMix = (cfg: DropGenTextMixInput): DropGenTextMix => clampMix({ proven: cfg.provenPerDrop, ai: cfg.amount });

/** Phase 38 — a hit-again drop keeps the texts of the drop that hit unless the policy gives the kind its own mix. */
export const keepsCopiedTexts = (cfg: DropGenTextMixInput, kind: DropGenDropKind): boolean =>
  DROP_GEN_COPY_KINDS.includes(kind) && (kind === "NORMAL" || kind === "ROUTE_TEST" || cfg.policy?.[kind] === undefined);

/** The mix for a kind — its own, else the ordinary one. */
export function mixFor(cfg: DropGenTextMixInput, kind: DropGenDropKind): DropGenTextMix {
  if (kind === "ROUTE_TEST" && cfg.routeTest) return clampMix(cfg.routeTest);
  const own = kind === "NORMAL" || kind === "ROUTE_TEST" ? undefined : cfg.policy?.[kind];
  return own ? clampMix(own) : ordinaryMix(cfg);
}

/** What the drop really sends given the pool's size: at most the pool's
 *  proven texts, and every proven text the pool cannot give becomes a
 *  fresh AI text (user, 13/10/2026: "if we don't have 10 best, then it will
 *  automatically be AI") — `short` says how many. A drop never goes text-less. */
export function effectiveMix(cfg: DropGenTextMixInput, kind: DropGenDropKind, poolSize: number): DropGenTextMix & { short: number } {
  const mix = mixFor(cfg, kind);
  const proven = Math.min(mix.proven, Math.max(0, poolSize));
  const short = mix.proven - proven;
  const ai = Math.min(DROP_GEN_TEXTS_PER_DROP_MAX - proven, mix.ai + short);
  if (proven === 0 && ai === 0) return { proven: 0, ai: Math.max(1, ordinaryMix(cfg).ai), short };
  return { proven, ai, short };
}

/** Any proven text anywhere — the pool is worth reading. */
export const anyProven = (cfg: DropGenTextMixInput): boolean =>
  ordinaryMix(cfg).proven > 0 || (cfg.routeTest?.proven ?? 0) > 0 || DROP_GEN_POLICY_KINDS.some((k) => (cfg.policy?.[k]?.proven ?? 0) > 0);

export const sameMix = (a: DropGenTextMix, b: DropGenTextMix): boolean => a.proven === b.proven && a.ai === b.ai;

/** "5 proven + 10 AI" · "5 proven only" · "10 AI only" · "no text". */
export function describeMix(m: DropGenTextMix): string {
  if (m.proven > 0 && m.ai > 0) return `${m.proven} proven + ${m.ai} AI`;
  if (m.proven > 0) return `${m.proven} proven only`;
  if (m.ai > 0) return `${m.ai} AI only`;
  return "no text";
}
